import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Select, MenuItem, Button, IconButton } from '@material-ui/core';
import { BorderColor, Close, ExpandLess, ExpandMore } from '@material-ui/icons';

import {
    getForm,
    createNewForm,
    applyFormToMedia,
    undoFormToMedia,
    getFormRegistrations,
    updateForm,
} from '../../actions/publishActions';
import { showNotification } from '../../actions/notificationActions';
import CreateForm, { STATE, FormType } from '../../components/CreateOrUpdateForm';
import { extractRequiredFieldKeys } from '../../components/DynamicForm/utils';
import { isValidUUID } from '../../utils/commonUtil';
import { getRoomName } from '../../utils/meetingUtils';
import styles from './FormsAndRegistrationScreen.module.scss';
import DetailsSection from './form/DetailsSection';
import ConversionsSection from './form/ConversionsSection';

const CREATE_NEW_FORM_KEY = 'create-new-form';
const LINK_TYPE_STANDARD = 'Form';
const LINK_TYPE_MEET = 'Form-meet';

interface FormPanelProps {
    formOptional?: boolean;
    formType: FormType;
    mediaId: string;
    mediaDetails: any;
    formTemplates: any[];
    linkedFormId: string;
    links: { type: string; id: string }[];
    onFormTemplatesChange: (_templates: any[]) => void;
}

const FormPanel = ({ formType, mediaId, mediaDetails, formTemplates, linkedFormId, links, onFormTemplatesChange }: FormPanelProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch() as any;
    const { defaultAccountId } = useSelector((state: any) => state.session);

    const SELECT_LABEL = t('COMMON_SELECT');
    const CREATE_NEW_FORM_LABEL = t('COMMON_CREATE_NEW_FORM');

    const filteredTemplates = useMemo(() => {
        return formTemplates.filter((f: any) => f.type === formType);
    }, [formTemplates, formType]);

    const [formTemplateId, setFormTemplateId] = useState<string>('');
    const [appliedFormTemplate, setAppliedFormTemplate] = useState<any | null>(null);
    const [registrations, setRegistrations] = useState<any[]>([]);
    const [initComplete, setInitComplete] = useState<boolean>(false);
    const [loadingRegistrations, setLoadingRegistrations] = useState<boolean>(false);
    const [hasMoreRegistrations, setHasMoreRegistrations] = useState<boolean>(false);
    const [regOffset, setRegOffset] = useState<number>(0);
    const [showFormPopup, setShowFormPopup] = useState<boolean>(false);
    const [showFormPicker, setShowFormPicker] = useState<boolean>(false);
    const [isFormSetupOpen, setIsFormSetupOpen] = useState<boolean>(false);
    const [isRegistrationsOpen, setIsRegistrationsOpen] = useState<boolean>(true);
    const [createdNewForm, setCreatedNewForm] = useState<boolean>(false);

    const selectForm = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        if (value === CREATE_NEW_FORM_KEY) {
            setShowFormPopup(true);
        } else {
            setFormTemplateId(value);
        }
    };

    const cleanupOldForm = (oldFormId: string) => {
        dispatch(getForm(defaultAccountId, oldFormId)).then((formData: any) => {
            if (!formData) { return; }
            const updatedMedias = Array.isArray(formData.medias)
                ? formData.medias.filter((m: string) => m !== mediaId)
                : [];
            const updatedData: any = { ...formData, medias: updatedMedias };
            if (formType === 'Meet') {
                const existingMeetDetails = Array.isArray(formData.meetDetails) ? formData.meetDetails : [];
                updatedData.meetDetails = existingMeetDetails.filter((e: any) => e.mediaId !== mediaId);
            }
            dispatch(updateForm(defaultAccountId, oldFormId, updatedData));
        });
    };

    const attachNewForm = (newFormId: string) => {
        dispatch(getForm(defaultAccountId, newFormId)).then((formData: any) => {
            if (!formData) { return; }
            const existingMedias = Array.isArray(formData.medias) ? formData.medias : [];
            const updatedData: any = { ...formData, medias: [...existingMedias, mediaId] };
            if (formType === 'Meet') {
                const existingMeetDetails = Array.isArray(formData.meetDetails) ? formData.meetDetails : [];
                const existingEntry = existingMeetDetails.find((e: any) => e.mediaId === mediaId);
                const configId = existingEntry?.configId || existingMeetDetails[0]?.configId || '';
                const newEntry = { configId, meetingId: getRoomName(mediaDetails), mediaId };
                updatedData.meetDetails = [
                    ...existingMeetDetails.filter((e: any) => e.mediaId !== mediaId),
                    newEntry,
                ];
            }
            dispatch(updateForm(defaultAccountId, newFormId, updatedData));
        });
    };

    const handleApplyChanges = () => {
        const id = appliedFormTemplate?.id || '';
        if (formTemplateId === id) { return; }

        const responseHandler = (respData: string, newId: string | null) => {
            if (isValidUUID(respData)) {
                const appliedTemplate = newId ? filteredTemplates.find((i: any) => i.id === newId) : null;
                setAppliedFormTemplate(appliedTemplate);
                if (newId) {
                    attachNewForm(newId);
                }
            } else {
                dispatch(showNotification(t('FORMS_AND_REGISTRATIONS_ERROR')));
            }
        };

        const linkType = formType === 'Meet' ? LINK_TYPE_MEET : LINK_TYPE_STANDARD;

        // Remove any duplicate links of this type beyond the one we're actively managing
        const staleIds = links
            .filter((l) => l.type === linkType && l.id !== id && l.id !== formTemplateId)
            .map((l) => l.id);
        staleIds.forEach((staleId) => {
            dispatch(undoFormToMedia(defaultAccountId, mediaId, staleId, linkType));
            cleanupOldForm(staleId);
        });

        if (formTemplateId) {
            if (id) {
                dispatch(undoFormToMedia(defaultAccountId, mediaId, id, linkType)).then((respData: string) => {
                    if (isValidUUID(respData)) {
                        cleanupOldForm(id);
                        dispatch(applyFormToMedia(defaultAccountId, mediaId, formTemplateId, linkType)).then((respData2: string) => {
                            responseHandler(respData2, formTemplateId);
                        });
                    }
                });
            } else {
                dispatch(applyFormToMedia(defaultAccountId, mediaId, formTemplateId, linkType)).then((respData: string) => {
                    responseHandler(respData, formTemplateId);
                });
            }
        } else {
            if (id) {
                dispatch(undoFormToMedia(defaultAccountId, mediaId, id, linkType)).then((respData: string) => {
                    if (isValidUUID(respData)) {
                        cleanupOldForm(id);
                    }
                    responseHandler(respData, null);
                });
            }
        }
    };

    const createNewFormTemplate = (data: any) => {
        const payload = {
            name: data.name,
            type: data.type,
            optional: data.optional,
            schema: {
                required: extractRequiredFieldKeys(data.uiSchema),
                ...data.schema,
            },
            styling: data.uiSchema,
        };
        dispatch(createNewForm(defaultAccountId, payload)).then((respData: any) => {
            if (respData && respData.id && respData.name) {
                const newFormId = respData.id;
                setFormTemplateId(newFormId);
                onFormTemplatesChange([...formTemplates, { ...respData }]);
                setCreatedNewForm(true);
            }
        });
        setShowFormPopup(false);
    };

    const formUpdateHandler = (data: any) => {
        try {
            if (!data || !appliedFormTemplate?.id) { return; }
            const id = appliedFormTemplate.id;
            dispatch(updateForm(defaultAccountId, id, data)).then((respData: any) => {
                if (respData && respData.id) {
                    setAppliedFormTemplate(respData);
                    dispatch(showNotification(t('INSPECT_MEDIA_MINI_FORM_PAGE_SUCCESS_MSG')));
                }
            });
        } catch (e) {
            console.error('Error updating form template', e);
        }
    };

    const getRegistrations = (searchKey: string = '') => {
        if (!appliedFormTemplate) { return; }
        const formId = appliedFormTemplate.id;
        setLoadingRegistrations(true);
        setRegOffset(0);
        dispatch(getFormRegistrations(defaultAccountId, formId, mediaId, searchKey, 0)).then((respData: any) => {
            if (respData && Array.isArray(respData)) {
                setRegistrations(respData);
                setHasMoreRegistrations(respData.length === 50);
            }
            setLoadingRegistrations(false);
        }).catch((e: any) => {
            setLoadingRegistrations(false);
            console.error('Failed to get the registrations:', e);
        });
    };

    const loadMoreRegistrations = (searchKey: string = ''): Promise<any[]> => {
        if (!appliedFormTemplate) { return Promise.resolve([]); }
        const formId = appliedFormTemplate.id;
        const nextOffset = regOffset + 50;
        return dispatch(getFormRegistrations(defaultAccountId, formId, mediaId, searchKey, nextOffset))
            .then((respData: any) => {
                if (respData && Array.isArray(respData)) {
                    setHasMoreRegistrations(respData.length === 50);
                    setRegOffset(nextOffset);
                    return respData;
                }
                return [];
            })
            .catch((e: any) => {
                console.error('Failed to load more registrations:', e);
                return [];
            });
    };

    useEffect(() => {
        if (createdNewForm) {
            handleApplyChanges();
            setCreatedNewForm(false);
        }
    }, [createdNewForm]);

    useEffect(() => {
        if (appliedFormTemplate && mediaId && defaultAccountId) {
            getRegistrations();
        }
    }, [appliedFormTemplate, mediaId, defaultAccountId]);

    useEffect(() => {
        setShowFormPicker(!appliedFormTemplate);
    }, [appliedFormTemplate]);

    useEffect(() => {
        if (!filteredTemplates.length || initComplete) { return; }
        const appliedTemplate = filteredTemplates.find((i: any) => i.id === linkedFormId);
        setAppliedFormTemplate(appliedTemplate || null);
        setFormTemplateId(linkedFormId);
        setInitComplete(true);
    }, [linkedFormId, filteredTemplates]);

    return (
        <div>
            {showFormPicker &&
                <>
                    <div className={styles.formPickerSelect}>
                        <div className={styles.formPickerSelectTitle}>
                            {t('FORMS_AND_REGISTRATIONS_CHOOSE_TEMPLATE')}
                            {appliedFormTemplate &&
                                <IconButton onClick={() => { setShowFormPicker(false); }}>
                                    <Close />
                                </IconButton>
                            }
                        </div>
                        <FormControl variant="outlined" className={styles.selectContainer} fullWidth>
                            <Select
                                value={formTemplateId}
                                onChange={selectForm}
                                displayEmpty
                                inputProps={{ className: styles.selectEl }}
                            >
                                <MenuItem value="">-{SELECT_LABEL}-</MenuItem>
                                <MenuItem key={CREATE_NEW_FORM_KEY} value={CREATE_NEW_FORM_KEY}>
                                    -{CREATE_NEW_FORM_LABEL}-
                                </MenuItem>
                                {filteredTemplates.map(item => (
                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className={styles.formPickerActions}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleApplyChanges}
                            disabled={appliedFormTemplate?.id === formTemplateId || (!appliedFormTemplate?.id && !formTemplateId)}
                        >
                            {t('COMMON_ATTACH_TO_MEDIA')}
                        </Button>
                    </div>
                </>
            }
            {appliedFormTemplate &&
                <>
                    {!showFormPicker &&
                        <div className={styles.appliedFormName}>
                            <div>{t('FORMS_AND_REGISTRATIONS_ATTACHED_FORM')}: <b>{appliedFormTemplate.name}</b></div>
                            <IconButton onClick={() => { setShowFormPicker(true); }} className={styles.appliedFormIcon}>
                                <BorderColor />
                            </IconButton>
                        </div>
                    }
                    <div className={styles.sectionContainer}>
                        <div className={styles.sectionTitle}>
                            {t('FORMS_AND_REGISTRATIONS_FORM_SETUP')}
                        </div>
                        <div>
                            <IconButton onClick={() => { setIsFormSetupOpen(prev => !prev); }}>
                                {isFormSetupOpen ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </div>
                    </div>
                    {isFormSetupOpen &&
                        <DetailsSection
                            formTemplate={appliedFormTemplate}
                            onChange={formUpdateHandler}
                        />
                    }
                    <div className={styles.sectionContainer}>
                        <div className={styles.sectionTitle}>
                            {t('FORMS_AND_REGISTRATIONS_REGISTRATIONS')}
                        </div>
                        <div>
                            <IconButton onClick={() => { setIsRegistrationsOpen(prev => !prev); }}>
                                {isRegistrationsOpen ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </div>
                    </div>
                    {isRegistrationsOpen &&
                        <ConversionsSection
                            formTemplate={appliedFormTemplate}
                            registrations={registrations}
                            isLoading={loadingRegistrations}
                            hasMore={hasMoreRegistrations}
                            onSearch={getRegistrations}
                            onChange={getRegistrations}
                            onLoadMore={loadMoreRegistrations}
                        />
                    }
                </>
            }
            <CreateForm
                open={showFormPopup}
                state={STATE._CREATE}
                defaultFormType={formType}
                hideFormType
                onClose={() => setShowFormPopup(false)}
                onSubmit={createNewFormTemplate}
            />
        </div>
    );
};

export default FormPanel;
